import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { CodeType } from "acquaint-api/constants/CodeType";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { PropType, computed, onMounted, ref, watch } from "vue";
import { useSystemCodesStore } from "@/stores/SystemCodesStore";
import singleSelect from "@/components/ui/dropdowns/single-select.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";


export default /*#__PURE__*/_defineComponent({
  __name: 'system-code-single-select',
  props: {
  /**
   * The numeric value used with `v-model` to bind the component state. This typically represents an ID or a selected value.
   * @type {number}
   * @required
   */
  modelValue: {
    type: Number,
    required: true,
  },
  /**
   * The type of code used to fetch system codes. This determines which set of codes will be retrieved and displayed in the multi-select component.
   * @type {CodeType}
   * @required
   */
  codeType: {
    type: Number as PropType<CodeType>,
    required: true,
  },
  /**
   * The label displayed for the control.
   * @type {String}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * Determines whether the control is read only.
   * @type {boolean}
   * @default true
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show Clear button.
   * @type {boolean}
   * @default true
   */
  showClearButton: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show search textbox.
   * @type {boolean}
   * @default true
   */
  showSearch: {
    type: Boolean,
    default: false,
  },
  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default: FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * An array of code IDs to exclude from the fetched system codes. This ensures that certain codes are not displayed in the selection list.
   * @type {Number[]}
   * @default []
   */
  idsToExclude: {
    type: Array as PropType<Number[]>,
    default() {
      return [];
    },
  },
  additionalIds: {
    type: Array as PropType<IDDescriptionPair[]>,
    default() {
      return [];
    },
  },
},
  emits: [
  /**
   * Emitted when the `modelValue` is updated, typically triggered when a selection changes.
   * @event update:modelValue
   * @param {Number} value - The new value to be updated in the model.
   */
  "update:modelValue",
  /**
   * Emitted when the value of the selection changes, with the updated description.
   * @event valueChanged
   * @param {string} value - The description of the selected value (or empty string if no selection).
   */
  "valueChanged",
],
  setup(__props, { emit }) {

const props = __props;



const systemCodesStore = useSystemCodesStore();
const systemCodes = ref<IDDescriptionPair[]>([]);

onMounted(async () => {
  // Gets all system codes for the passed code type so that they can be displayed drop of of the multi select component
  await fetchSystemCodes();
  systemCodes.value = [...systemCodes.value, ...props.additionalIds];
});

const fetchSystemCodes = async () => {
  try {
    let codes = await systemCodesStore.getSystemCodesByCodeType(props.codeType);

    if (props.idsToExclude.length > 0) {
      codes = codes.filter((code) => !props.idsToExclude.includes(code.id));
    }

    systemCodes.value = codes;
  } catch (error) {
    displayErrorMessage(getErrorMessage(error));
  }
};

// Watch for changes in codeType (handles modal re-open scenario)
watch(() => props.codeType, fetchSystemCodes, { immediate: true });



const selectedValueID = computed({
  get: () => props.modelValue,
  set: (value: Number) => emit("update:modelValue", value),
});

watch(
  () => selectedValueID.value,
  () => {
    let selectedValue = systemCodes.value.filter((idDescriptionPair) => {
      return idDescriptionPair.id == selectedValueID.value;
    });
    emit(
      "valueChanged",
      selectedValue.length > 0 ? selectedValue[0].description : "",
    );
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(singleSelect, {
    modelValue: selectedValueID.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedValueID).value = $event)),
    "data-source": systemCodes.value,
    readOnly: props.readOnly,
    label: __props.label,
    showClearButton: props.showClearButton,
    showSearch: props.showSearch,
    "allow-zero-value": true,
    width: __props.width
  }, null, 8, ["modelValue", "data-source", "readOnly", "label", "showClearButton", "showSearch", "width"]))
}
}

})